/**
 * formDexRoster — the Regional and Costume dexes (2026-08-31 ask).
 *
 * Two new manual-only dexes whose CELLS are forms, not species. Every other dex
 * in the app renders one cell per species; these render one cell per FORM, so
 * Meowth appears twice on the Regional dex (Alolan and Galarian) and Bulbasaur
 * four times on the Costume dex. The store already keys entries by
 * (variant, dexNumber, formId) — the same rows Mega X/Y and the Dynamax page
 * ride — so a form cell needs no new storage, sync or cascade machinery.
 *
 * Both rosters are DERIVED from the generated forms registry
 * (pokemonForms.generated.ts) rather than hand-listed, so a monthly refresh that
 * adds a costume adds a cell with no code change. Derivation needs a curation
 * seam in exactly one place — what counts as a REGIONAL form — because PokéAPI
 * does not label them: `kind` splits form from costume, and nothing splits
 * regional from pattern (Vivillon) or mechanical (Giratina). The rule below is
 * name-based with an explicit include/exclude list beside it, and
 * formDexRoster.test.ts pins the resulting roster so a refresh that changes it
 * fails loudly rather than silently reshaping a trainer's dex.
 */
import {
  FORM_GROUPS_BY_SPECIES_ID,
  POKEMON_FORM_GROUPS,
} from "./generated/pokemonForms.generated";
import { isFormVariantReleased, type FormVariant } from "./types/forms";
import { FORM_DEX_VARIANTS, type PokedexVariant } from "./pogoPokedexVariantClassifier";
import { sortCostumesNewestFirst } from "./costumeReleaseOrder";

/** True for the dexes whose cells are forms rather than species. */
export function isFormDexVariant(variant: PokedexVariant): boolean {
  return (FORM_DEX_VARIANTS as readonly PokedexVariant[]).includes(variant);
}

/**
 * A regional form's slug carries its region as a segment: `rattata-alola`,
 * `tauros-paldea-combat-breed`, `darmanitan-galar-standard`. Matched on the
 * SEGMENT, not a substring, so nothing accidental gets in.
 */
const REGION_SEGMENT = /(^|-)(alola|galar|hisui|paldea)(-|$)/;

/**
 * Regional forms the name rule cannot reach. Hisuian Basculin is filed under
 * its stripe colour (`basculin-white-striped`), not its region — it is the
 * White-Striped Basculin that evolves into Basculegion, and GO lists it with
 * the other Hisuian forms.
 */
const REGIONAL_INCLUDE_FORM_IDS: ReadonlySet<string> = new Set([
  "basculin-white-striped",
]);

/**
 * Slugs the name rule catches but that are not regional forms. `pikachu-alola-cap`
 * is an Ash cap: PokéAPI files the caps as forms rather than costumes, and the
 * cap is the point of it, not the region.
 */
const REGIONAL_EXCLUDE_FORM_IDS: ReadonlySet<string> = new Set([
  "pikachu-alola-cap",
]);

export function isRegionalForm(form: FormVariant): boolean {
  if (form.kind === "costume") return false;
  if (REGIONAL_EXCLUDE_FORM_IDS.has(form.formId)) return false;
  if (REGIONAL_INCLUDE_FORM_IDS.has(form.formId)) return true;
  return REGION_SEGMENT.test(form.formId);
}

/** One grid cell of a form dex: a single form of a single species. */
export interface FormDexCell {
  /** The base species' national dex number — the cell's number and its store key. */
  dexNumber: number;
  /** Registry form id, the second half of the store key ("meowth-galar"). */
  formId: string;
  /** What the cell says under the art ("Galar", "Fall 2019"). */
  label: string;
  /** Non-shiny art for the caught cell. */
  spritePath: string;
  /** Obtainable in GO today: an unreleased cell renders locked, like every dex. */
  released: boolean;
}

function buildRoster(
  pick: (f: FormVariant) => boolean,
  order: (forms: FormVariant[]) => FormVariant[] = (forms) => forms,
): FormDexCell[] {
  const cells: FormDexCell[] = [];
  for (const group of POKEMON_FORM_GROUPS) {
    for (const form of order(group.forms.filter(pick))) {
      cells.push({
        dexNumber: group.baseDexId,
        formId: form.formId,
        label: form.displayName,
        spritePath: form.spritePath,
        released: isFormVariantReleased(form),
      });
    }
  }
  // Dex order, then the caller's order within a species (registry order unless
  // it says otherwise) — the same reading order as every other dex, so the
  // region row-break lands where it does everywhere else. A stable sort, so
  // the within-species order survives it.
  return cells.sort((a, b) => a.dexNumber - b.dexNumber);
}

/** Every regional form in the registry, in dex order. */
export const REGIONAL_DEX_CELLS: readonly FormDexCell[] = buildRoster(isRegionalForm);

/**
 * Every GO event costume in the registry, in dex order, and within a species
 * NEWEST FIRST by GO release (2026-10-20 owner ask): the costume still out
 * there to catch leads Pikachu's run of cells, the 2016 Santa hat closes it.
 */
export const COSTUME_DEX_CELLS: readonly FormDexCell[] = buildRoster(
  (f) => f.kind === "costume",
  sortCostumesNewestFirst,
);

const CELLS_BY_VARIANT: Partial<Record<PokedexVariant, readonly FormDexCell[]>> = {
  regional: REGIONAL_DEX_CELLS,
  costume: COSTUME_DEX_CELLS,
};

/** Index for the per-species lookup the grid does once per rendered species. */
const CELLS_BY_VARIANT_AND_SPECIES = new Map<string, FormDexCell[]>();
for (const [variant, cells] of Object.entries(CELLS_BY_VARIANT)) {
  for (const cell of cells ?? []) {
    const key = `${variant}:${cell.dexNumber}`;
    const list = CELLS_BY_VARIANT_AND_SPECIES.get(key);
    if (list) list.push(cell);
    else CELLS_BY_VARIANT_AND_SPECIES.set(key, [cell]);
  }
}

/** Every cell of a form dex, in grid order. Empty for any other variant. */
export function formDexCells(variant: PokedexVariant): readonly FormDexCell[] {
  return CELLS_BY_VARIANT[variant] ?? [];
}

/**
 * The cells a species occupies on a form dex, left to right. Empty when the
 * species has no form on that dex (or the variant is not a form dex), which is
 * what makes this safe to call for every cell the grid renders.
 */
export function formDexCellsFor(
  variant: PokedexVariant,
  dexNumber: number,
): readonly FormDexCell[] {
  return CELLS_BY_VARIANT_AND_SPECIES.get(`${variant}:${dexNumber}`) ?? [];
}

/** The species ids on a form dex, ascending and de-duplicated — its roster. */
export function formDexSpeciesIds(variant: PokedexVariant): readonly number[] {
  return Array.from(new Set(formDexCells(variant).map((c) => c.dexNumber)));
}

/**
 * Colour art for a form cell. The registry path IS the art, so this never has
 * to guess: `speciesFormArtCandidates` resolves the same path from the same
 * registry, and going through it keeps the CDN fallback chain (official artwork
 * → home → pokesprite) identical to every other form surface.
 */
export function formDexCell(
  variant: PokedexVariant,
  dexNumber: number,
  formId: string | null | undefined,
): FormDexCell | null {
  if (!formId) return null;
  return formDexCellsFor(variant, dexNumber).find((c) => c.formId === formId) ?? null;
}

/**
 * The registry form behind a cell, for surfaces that want the full record
 * (shiny art, release date). Null when the id is not a form of that species.
 */
export function formDexRegistryForm(
  dexNumber: number,
  formId: string,
): FormVariant | null {
  return (
    FORM_GROUPS_BY_SPECIES_ID.get(dexNumber)?.forms.find((f) => f.formId === formId) ??
    null
  );
}

/**
 * One-shot hand-off from a form dex to the species page (2026-09-01 owner
 * report: the Regional and Costume dexes "aren't linked to the species pages").
 *
 * Tapping an Alolan Meowth cell used to land on plain Meowth with nothing to
 * say which of its cells you came from, on a page whose Forms section may be
 * collapsed and whose Costumes section can be 54 tiles long. The grid ARMS the
 * form id right before it navigates; the species page CONSUMES it on mount,
 * opens the section that form lives in and rings the cell.
 *
 * Module state, deliberately: the hand-off exists only inside one in-app
 * navigation, so arriving at the same species any other way opens the page
 * exactly as before. Same pattern as armDynamaxDetailView.
 */
let pendingFormDetailView: string | null = null;

export function armFormDetailView(formId: string): void {
  pendingFormDetailView = formId;
}

/** Read-and-clear. Null when nothing was armed. */
export function consumeFormDetailView(): string | null {
  const v = pendingFormDetailView;
  pendingFormDetailView = null;
  return v;
}
